import {
  type QueryClient,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import api, { apiWithoutCamelization } from "../../../api";
import { mieterstromInvoicingApiWithoutCamelization } from "../../../api/subApis/mieterstromInvoicingApi";
import {
  type ShowErrorToastFn,
  useToast
} from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import {
  pollTaskStatus,
  postDataAndPollResponse
} from "../../../utils/api-utils";
import type { InvoiceState } from "../../../utils/backend-types";
import type { Contract } from "../../DeliveryOverview/DeliveryOverview.types";
import type { PendingInvoice } from "./usePendingInvoices";

interface UpdateInvoiceStateData {
  id: string;
  state: InvoiceState;
}

export function useInvoiceMutations(projectId: string) {
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();

  const generateInvoices = useMutation({
    mutationFn: async () => {
      return generateInvoicesFn(projectId);
    },
    onSettled: () => {
      invalidateRelevantQueries(queryClient, projectId);
      queryClient.invalidateQueries({
        queryKey:
          mieterstromInvoicingApiWithoutCamelization.contracts.key(projectId)
      });
    }
  });

  const generateSingleInvoice = useMutation({
    mutationFn: async (pendingInvoice: PendingInvoice) => {
      return generateSingleInvoiceFn(pendingInvoice);
    },
    onSettled: () => {
      invalidateRelevantQueries(queryClient, projectId);
      queryClient.invalidateQueries({
        queryKey:
          mieterstromInvoicingApiWithoutCamelization.contracts.key(projectId)
      });
    }
  });

  const sendMail = useMutation({
    mutationFn: (invoiceId: string) => sendMailFn(invoiceId),
    onSettled: () => {
      invalidateRelevantQueries(queryClient, projectId);
    }
  });

  const setMailAllowance = useMutation({
    mutationFn: (contract: Contract) => setMailAllowanceFn(contract),
    onSettled: (_data, _error, contract) => {
      queryClient.invalidateQueries({
        queryKey: mieterstromInvoicingApiWithoutCamelization.contracts.key(
          contract.project
        )
      });
    }
  });

  const updateInvoiceState = useMutation({
    mutationFn: ({ id, state }: UpdateInvoiceStateData) =>
      updateInvoiceStateFn(id, state),
    onSettled: () => {
      invalidateRelevantQueries(queryClient, projectId);
    }
  });

  const reverseInvoice = useMutation({
    mutationFn: (id: string) =>
      reverseInvoiceFn(showErrorToast, id, queryClient, projectId)
  });

  return {
    generateInvoices,
    generateSingleInvoice,
    reverseInvoice,
    sendMail,
    setMailAllowance,
    updateInvoiceState
  };
}

function generateInvoicesFn(projectId: string) {
  return postDataAndPollResponse(urls.api.generateInvoices(projectId), {});
}

function generateSingleInvoiceFn(pendingInvoice: PendingInvoice) {
  return postDataAndPollResponse(
    urls.api.generateSingleInvoice(),
    pendingInvoice
  );
}

function sendMailFn(id: string) {
  return api.post(urls.api.sendInvoiceToSupplied(id));
}

function setMailAllowanceFn(contract: Contract) {
  return apiWithoutCamelization.put<void>(
    mieterstromInvoicingApiWithoutCamelization.contract.url(contract.id),
    {
      ...contract,
      allowance_send_invoice_via_mail: true
    }
  );
}

function updateInvoiceStateFn(invoiceId: string, state: InvoiceState) {
  return api.post(urls.api.invoiceUpdateState(invoiceId), { state });
}

async function reverseInvoiceFn(
  showErrorToast: ShowErrorToastFn,
  invoiceId: string,
  queryClient: QueryClient,
  projectId: string
) {
  try {
    const response = await api.post(urls.api.archiveInvoice(invoiceId));
    if (response.data) {
      return await new Promise<void>((resolve, reject) => {
        pollTaskStatus(
          urls.api.jobStatus(response.data.taskIds[0]),
          () => {
            resolve();
            invalidateRelevantQueries(queryClient, projectId);
          },
          (error) => {
            reject(error);
          }
        );
      });
    }
    return;
  } catch (error) {
    showErrorToast(error);
  }
}

function invalidateRelevantQueries(
  queryClient: QueryClient,
  projectId: string
) {
  queryClient.invalidateQueries({
    queryKey: ["invoices", { projectId }]
  });
  queryClient.invalidateQueries({
    queryKey:
      mieterstromInvoicingApiWithoutCamelization.pendingInvoices.key(projectId)
  });
}
